// The company's jobs as the web holds them (Jobs and Schedule v1, stage 6):
// whether jobs are on, the list as GET /jobs last served it, one job whole,
// and the people a visit can be for.
//
// Each job the API serves carries what it is now beside it (`status`,
// `money`, `nextStep`, `rowDetail`, `mayDelete`, server/jobs.js's
// withSummary), worked out by shared/jobs.js. Nothing here works any of it
// out again: a job read from here says what the server said.
//
// The Schedule (stage 6, its own PR) reads the same: `state.jobs` carries
// every job's visits, and `loadVisitPeople()` the people a visit can be
// given to, in the shape shared/team.js's rules take.

import { state } from "./state.js";
import { api } from "./api.js";
import { roleHasCapability } from "./roles.js";
import { initialsFor } from "../../../shared/names.js";

/** Whether the server has switched jobs on for this company (`features.jobs`, ADR-039). */
export function jobsOn() {
  return state.features?.jobs === true;
}

/**
 * Where the web opens: the Schedule while jobs are on, as the iPhone's first
 * tab is, else Invoices. After signing in, on "/", and from the wordmark.
 */
export function homeRoute() {
  return jobsOn() ? "/schedule" : "/invoices";
}

/**
 * Every job, archived ones too, from GET /jobs?archived=1, into `state.jobs`.
 * The list filters them itself, so one request answers every Show choice.
 */
export async function loadJobs() {
  const data = await api("/jobs?archived=1");
  state.jobs = Array.isArray(data?.jobs) ? data.jobs : [];
  state._loaded.jobs = true;
  return state.jobs;
}

/** The jobs as last loaded, or loaded now when they haven't been. Never throws. */
export async function ensureJobs() {
  if (!jobsOn()) return [];
  if (state._loaded.jobs) return state.jobs;
  try {
    return await loadJobs();
  } catch {
    return state.jobs;
  }
}

/** Put a job the server just answered with into the list, in place of the one it was. */
export function keepJob(job) {
  if (!job?.id) return;
  const rest = (state.jobs || []).filter((existing) => existing.id !== job.id);
  // The list's rows carry no quote or invoices; a job's own answer does, and
  // is the newer of the two.
  state.jobs = [job, ...rest];
}

/** Take a deleted job out of the list. */
export function forgetJob(jobId) {
  state.jobs = (state.jobs || []).filter((job) => job.id !== jobId);
}

/** One job, whole (GET /jobs/:id): its visits, quote, invoices and credit notes. */
export async function loadJob(jobId) {
  const data = await api(`/jobs/${encodeURIComponent(jobId)}`);
  if (data?.job) keepJob(data.job);
  return data?.job || null;
}

/** The job made from this quote, if the list knows one (one job a quote, ADR-039). */
export function jobForQuote(quoteId) {
  if (!quoteId) return null;
  return (state.jobs || []).find((job) => job.quoteId === quoteId) || null;
}

/** Changes that move a job's status or money: the list is read again next time. */
export function jobsChanged() {
  state._loaded.jobs = false;
}

/**
 * The company's clients, loaded once (GET /contacts) for what a job and a
 * visit say about them: who to ask for, and who Tell the client emails.
 * Never throws: a form says what it can with what there is.
 */
export async function ensureContacts() {
  if (state._loaded.contacts) return state.contacts || [];
  try {
    const data = await api("/contacts");
    if (Array.isArray(data?.contacts)) {
      state.contacts = data.contacts;
      state._loaded.contacts = true;
    }
  } catch {
    // What there is.
  }
  return state.contacts || [];
}

let people = null;

/**
 * The company's active people, as shared/team.js's rules take them (`{
 * userId, name, initials, role, mayWorkVisits }`, the shape `/sync/pull`
 * serves, the initials by shared/names.js as the server makes them), from
 * GET /team. Two or more of them is a team (`isTeam`). Empty when the team
 * can't be read, which shows nothing a team would.
 */
export async function loadVisitPeople() {
  if (people) return people;
  try {
    const data = await api("/team");
    people = (Array.isArray(data?.team) ? data.team : [])
      .filter((member) => member?.status === "active" && member.userId)
      .map((member) => ({
        userId: member.userId,
        // Their page on Team, which a visit's Assigned to opens.
        membershipId: member.id,
        name: member.displayName || member.name || "Member",
        initials: initialsFor(member),
        role: member.role,
        mayWorkVisits: roleHasCapability(member.role, "visits.work")
      }));
  } catch {
    return [];
  }
  return people;
}

/** The people as last loaded, or [] before they have been. */
export function cachedVisitPeople() {
  return people || [];
}
